import React, { useState, useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useRowLinkBuilder } from '../../../lib/useRowLink';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Page as PageLayout,
  DataTable,
  EmptyState,
  LoadingOrbit,
  Text,
  Button,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { ClipboardText, Play } from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import {
  useExploratoryTests,
  StartExploratoryTestDialog,
  getExploratoryTestStatusMeta,
} from '../../../features/exploratory-tests';
import type {
  ExploratoryTestListItem,
  ExploratoryTestStatus,
} from '../../../features/exploratory-tests';
import { ExploratoryTestNameCell } from './ExploratoryTestsPage';

const formatDuration = (durationMs: number | null | undefined): string => {
  if (!durationMs) return '-';
  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
};

/**
 * Labs › Spec Review — the ungraded test-spec feasibility walkthrough flavor
 * of the exploratory primitive. Reuses the exploratory list data + launcher +
 * detail page, scoped to `flavor: 'spec'` (the flag cuts through the shared
 * UI). Unlike UX Review, there is no letter grade — the run is judged item by
 * item, not holistically, so the list surfaces the summary instead.
 */
export function SpecReviewPage() {
  const navigate = useNavigate();
  const buildHref = useRowLinkBuilder();
  const search = useSearch({ from: '/runs/spec-review' }) as { page: number; pageSize: number };
  const page = search.page;
  const pageSize = search.pageSize;
  const [dialogOpen, setDialogOpen] = useState(false);

  useBreadcrumbs([{ key: 'spec-review', label: 'Spec Review' }]);

  const handlePageChange = useCallback(
    (nextPage: number) => {
      navigate({
        to: '/runs/spec-review',
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, page: nextPage };
          if (nextPage === 1) delete next.page;
          return next;
        },
      });
    },
    [navigate]
  );

  const testsQuery = useExploratoryTests({ page, pageSize, flavor: 'spec' });

  const newButton = (size: 'sm' | 'md') => (
    <Button
      size={size}
      variant="primary"
      iconLeft={<Play size={size === 'sm' ? 14 : 16} weight="fill" />}
      onPress={() => setDialogOpen(true)}
    >
      New spec review
    </Button>
  );

  const columns: ColumnDef<StockFeatures, ExploratoryTestListItem>[] = React.useMemo(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const meta = getExploratoryTestStatusMeta(row.original.status as ExploratoryTestStatus);
          return (
            <TagBadge name={meta.label} color={badgeVariantToTagColor(meta.badgeVariant)} hideDot />
          );
        },
        meta: { flex: 0.8 },
      },
      {
        id: 'name',
        header: 'Spec',
        cell: ({ row }) => <ExploratoryTestNameCell test={row.original} />,
        meta: { flex: 2.5 },
      },
      {
        accessorKey: 'propertyName',
        header: 'Application',
        cell: ({ row }) => (
          <Text size="sm" className="truncate text-muted-foreground">
            {row.original.propertyName}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
      {
        accessorKey: 'issuesFound',
        header: 'Findings',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.issuesFound}
          </Text>
        ),
        meta: { flex: 0.7 },
      },
      {
        id: 'summary',
        header: 'Summary',
        cell: ({ row }) => (
          <Text size="sm" className="truncate text-muted-foreground">
            {row.original.summary ?? '—'}
          </Text>
        ),
        meta: { flex: 2.5 },
      },
      {
        id: 'duration',
        header: 'Duration',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatDuration(row.original.durationMs)}
          </Text>
        ),
        meta: { flex: 0.8 },
      },
      {
        accessorKey: 'createdAt',
        header: 'Started',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.startedAt
              ? new Date(row.original.startedAt).toLocaleString()
              : new Date(row.original.createdAt).toLocaleString()}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
    ],
    []
  );

  return (
    <PageLayout>
      {testsQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : testsQuery.data && testsQuery.data.items.length > 0 ? (
        <>
          <div className="mb-4 flex justify-end">{newButton('md')}</div>
          <DataTable
            columns={columns}
            data={testsQuery.data.items}
            aria-label="Spec reviews table"
            serverPagination={{
              page,
              pageSize,
              pageCount: testsQuery.data.pagination.totalPages,
              totalItems: testsQuery.data.pagination.totalItems,
              onPageChange: handlePageChange,
            }}
            getRowHref={(test) =>
              buildHref({
                to: '/runs/spec-review/$exploratoryTestId',
                params: { exploratoryTestId: test.id },
              })
            }
          />
        </>
      ) : (
        <EmptyState
          icon={<ClipboardText size={48} />}
          title="No spec reviews yet"
          description="A spec review walks a loose test spec through your live app and reports, item by item, what's testable, what's not, and why — with a cleaned-up, runnable spec at the end."
          action={newButton('sm')}
        />
      )}

      {dialogOpen && (
        <StartExploratoryTestDialog
          isOpen={dialogOpen}
          onOpenChange={setDialogOpen}
          defaultFlavor="spec"
          lockFlavor
        />
      )}
    </PageLayout>
  );
}
